import React from 'react';
import * as Yup from 'yup';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from '@/components/buttons/Button';
import Form from '@/components/form/Form';

import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import PromptMessage from '@/components/UI/PromptMessage';
import PhoneNumberField from '@/components/form/phone-number/PhoneNumberField';
import { MobileInputModel } from '@/models/mobile-input.model';
import { useLogin } from '@/hooks/useLogin';
import BffApiService from '@/shared/bffApiService';
import { generateBffErrorWithTranslationId } from '@/utils/utils';
import { LoginFlowVariant } from '@/store/authentication/AuthenticationStore';

const initialValues = {
  mobile: {
    number: '',
    country: 'TH',
    prefix: '+66',
    wholeNumber: '',
  },
};

const ForgotPasswordSmsForm = ({
  onSuccessCallback,
}: {
  onSuccessCallback: (mobile: { mobile: string; mobilePrefix: string }) => void;
}) => {
  const intl = useIntl();
  const { request, loading, error } = useBffApiRequest();
  const { openLogin } = useLogin();

  const validationSchema = Yup.object().shape({
    mobile: Yup.object().shape({
      wholeNumber: Yup.string().required(intl.formatMessage({ id: 'input.error.required' })),
      number: Yup.string().min(5, intl.formatMessage({ id: 'inputs.mobile.invalid' })),
    }),
  });

  const successCallback = (mobile: { mobile: string; mobilePrefix: string }) => () => {
    triggerGTag(GTagEvents.forgot_password_sms_success);
    onSuccessCallback(mobile);
  };

  const errorCallback = () => {
    triggerGTag(GTagEvents.forgot_password_sms_error);
  };

  const handleSubmit = ({ mobile }: { mobile: MobileInputModel }) => {
    triggerGTag(GTagEvents.forgot_password_sms_submit);
    request({
      apiMethod: BffApiService.forgotPasswordSms,
      payload: { mobile: mobile.number, mobilePrefix: mobile.prefix },
      successCallback: successCallback({
        mobile: mobile.number,
        mobilePrefix: mobile.prefix,
      }),
      errorCallback,
    });
  };

  const openLoginClicked = () => {
    openLogin({ openedFrom: OpenedFrom.ForgotPasswordSms, variant: LoginFlowVariant.Mobile });
  };

  return (
    <Form
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col w-full justify-between gap-7"
    >
      {() => (
        <>
          <PhoneNumberField
            label={intl.formatMessage({ id: 'inputs.mobile' })}
            name="mobile"
            onFocus={() => triggerGTag(GTagEvents.login_via_sms_mobile_focus)}
            onValidationError={error =>
              triggerGTag(GTagEvents.login_via_sms_mobile_error, { error })
            }
            className="border-[1px] border-white/30 text-white focus-within:border-blue-light
                [&_input]:placeholder:!text-white/60"
          />
          <div className="flex flex-col items-center mt-4">
            {error && (
              <PromptMessage
                type="error"
                message={generateBffErrorWithTranslationId(intl, 'error.support', error.code)}
              />
            )}
            <Button
              text={intl.formatMessage({ id: 'misc.submit' })}
              type="submit"
              btnType="primary"
              data-cy="forgot-password-submit-button"
              isLoading={loading}
            />
            <div className="text-white text-[15px] text-center mt-6">
              <FormattedMessage
                id="authentication.forgot-password.change-to-login"
                values={{
                  a: str => (
                    <span
                      onClick={openLoginClicked}
                      className="text-blue-light font-bold underline cursor-pointer"
                    >
                      {str}
                    </span>
                  ),
                }}
              />
            </div>
            <p className="max-w-[80%] mt-3 block !text-[12px] italic text-white mb-0">
              {intl.formatMessage({ id: 'authentication.forgot-password-sms.description' })}
            </p>
          </div>
        </>
      )}
    </Form>
  );
};

export default ForgotPasswordSmsForm;
